import type { ProductVisibility } from "@/types/drop";

export type { ProductVisibility };
export type AvailabilityDate = Date | string | null | undefined;
import { getEffectiveReleaseDate } from "@/lib/commerce/early-access-pure";

export type AvailabilityDrop = {
  release_date: AvailabilityDate;
  final_date?: AvailabilityDate;
  product_visibility?: ProductVisibility | null;
  is_enabled?: boolean | null;
};

export type DropStatus = "upcoming" | "live" | "ended" | "disabled";

export type AvailabilityProduct = {
  is_enabled?: boolean | null;
  is_preorder?: boolean | null;
  drop?: AvailabilityDrop | null;
};

function toTimestamp(value: AvailabilityDate): number | null {
  if (!value) return null;
  const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function getDropStatus(drop: AvailabilityDrop, now = new Date()): DropStatus {
  if (drop.is_enabled === false) return "disabled";

  const nowTimestamp = now.getTime();
  const releaseTimestamp = toTimestamp(drop.release_date);
  const finalTimestamp = toTimestamp(drop.final_date);

  if (releaseTimestamp !== null && releaseTimestamp > nowTimestamp) return "upcoming";
  if (finalTimestamp !== null && finalTimestamp <= nowTimestamp) return "ended";
  return "live";
}

export function getEffectiveDropStatus(
  drop: AvailabilityDrop,
  hoursHeadStart: number,
  now = new Date()
): DropStatus {
  if (drop.is_enabled === false) return "disabled";
  const effectiveRelease = getEffectiveReleaseDate(drop.release_date as string, hoursHeadStart);
  const effectiveTimestamp = toTimestamp(effectiveRelease);
  const finalTimestamp = toTimestamp(drop.final_date);
  const nowTimestamp = now.getTime();
  if (effectiveTimestamp !== null && effectiveTimestamp > nowTimestamp) return "upcoming";
  if (finalTimestamp !== null && finalTimestamp <= nowTimestamp) return "ended";
  return "live";
}

export function isDropAvailableWithEarlyAccess(
  drop: AvailabilityDrop,
  hoursHeadStart: number,
  now = new Date()
): boolean {
  return getEffectiveDropStatus(drop, hoursHeadStart, now) === "live";
}

export function isDropAvailable(drop: AvailabilityDrop, now = new Date()): boolean {
  return getDropStatus(drop, now) === "live";
}

export function getProductVisibility(
  product: AvailabilityProduct,
  now = new Date()
): ProductVisibility | undefined {
  if (!product.drop || getDropStatus(product.drop, now) !== "upcoming") return undefined;
  return product.drop.product_visibility ?? "none";
}

export function isPreorderOpen(product: AvailabilityProduct, now = new Date()): boolean {
  const visibility = getProductVisibility(product, now);
  if (visibility === "preview" || visibility === "teaser") return false;
  if (!product.is_preorder && visibility !== "preorder") return false;
  return !product.drop || getDropStatus(product.drop, now) === "upcoming";
}

export function isProductShopListable(product: AvailabilityProduct, now = new Date()): boolean {
  if (product.is_enabled === false) return false;
  if (!product.drop) return true;
  const dropStatus = getDropStatus(product.drop, now);
  if (dropStatus === "disabled") return false;
  const visibility = getProductVisibility(product, now);
  return visibility !== "none" && visibility !== "preview" && visibility !== "teaser";
}

export function isProductAvailable(product: AvailabilityProduct, now = new Date()): boolean {
  if (product.is_enabled === false) return false;
  if (!product.drop) return true;
  const visibility = getProductVisibility(product, now);
  if (visibility === "preview" || visibility === "teaser" || visibility === "none") return false;
  if (isPreorderOpen(product, now)) return true;
  return isDropAvailable(product.drop, now);
}

export function isProductViewable(product: AvailabilityProduct, now = new Date()): boolean {
  if (product.is_enabled === false) return false;
  if (!product.drop) return true;
  const dropStatus = getDropStatus(product.drop, now);
  if (dropStatus === "disabled") return false;
  if (dropStatus === "ended") return true;
  const visibility = getProductVisibility(product, now);
  if (visibility === "none" || visibility === "teaser") return false;
  return true;
}
